import React from 'react';
import { Button, Caption1, Divider, mergeClasses } from '@fluentui/react-components';
import { Navigation20Regular } from '@fluentui/react-icons';
import { useSideNavStyles } from '../styles/SideNav.styles';
import { useAppRoles } from '../hooks/useAppRoles';
import { APP_ROUTE_GROUPS } from '../constants/appRoutes';

interface SideNavProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export const SideNav: React.FC<SideNavProps> = ({ currentPath, onNavigate, isOpen, onToggle }) => {
  const styles = useSideNavStyles();
  const { hasAnyRole } = useAppRoles();

  const navigationGroups = APP_ROUTE_GROUPS
    .map((group) => ({
      ...group,
      items: group.routes.filter((route) => !route.hideFromNav && hasAnyRole(route.roles)),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className={mergeClasses(styles.sidebar, !isOpen && styles.sidebarCollapsed)}>
      <div className={styles.sidebarHeader}>
        <Button
          appearance="subtle"
          icon={<Navigation20Regular />}
          size="large"
          onClick={onToggle}
          aria-label={isOpen ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={isOpen}
        />
      </div>
      {isOpen && (
        <nav>
          {navigationGroups.map((group) => (
            <div key={group.label} className={styles.navGroup}>
              <Caption1 className={styles.navGroupLabel}>{group.label}</Caption1>
              <Divider />
              {group.items.map((item) => (
                <Button
                  key={item.path}
                  appearance={currentPath === item.path ? 'primary' : 'subtle'}
                  icon={item.icon}
                  className={styles.navItem}
                  onClick={() => onNavigate(item.path)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          ))}
        </nav>
      )}
    </div>
  );
};
